// Level video (Phelipe 2026-09-25, Site Walks list #6): the camera tilts in
// the walker's hand, so the horizon wobbles. The Insta360 X5 keeps which way
// was up for it at every picture (SiteWalkPlayback.level_track: the up
// direction in the camera's axes - x the middle of the stitched picture, y
// its left, z the top of the camera). The 360° view turns each picture back
// level: it looks along the rays of a level camera facing the same way, and
// finds where each lands in the tilted picture. Pure maths, no DOM; tested
// in test/javascript/level_test.mjs.
//
// Steady (Phelipe 2026-09-26, "do #6 steady as well"): the track also says
// how far the camera had turned at each frame, so the view keeps pointing the
// same way when the camera turns in the walker's hand, the compass bearing
// the middle of the picture faced, and when on the GPS route each frame was
// really taken (SiteWalkSteady). Rows: [frame or seconds, up x, up y, up z,
// turn, bearing, route seconds].
//
// Walks read before that have the older track ({ by: "picture", up: [...] }):
// the up directions averaged over a few pictures either side, as the copy can
// sit a picture or more off the camera's own list, and no turns.
const AROUND = 4

// What the track says for `seconds` of the copy that plays: { up, turn,
// bearing, route } (turn in degrees to the left since the start, bearing in
// compass degrees, route in seconds along the GPS route; each null where the
// track doesn't say), or null.
export function trackAt(track, seconds, frameSeconds = 0) {
  if (track?.by === "picture") {
    const up = upAt(track, seconds, frameSeconds)
    return up ? { up, turn: null, bearing: null, route: null } : null
  }
  const rows = track?.rows
  if (!Array.isArray(rows) || rows.length === 0) return null
  let row = null
  if (track.by === "frame") {
    if (!(frameSeconds > 0)) return null
    track._byFrame ||= new Map(rows.map((r) => [r[0], r]))
    row = track._byFrame.get(Math.floor(seconds / frameSeconds + 1e-3)) || null
  } else {
    row = nearestRow(rows, seconds)
    if (row && Math.abs(row[0] - seconds) > 1) row = null
  }
  if (!row) return null
  const number = (v) => (Number.isFinite(v) ? v : null)
  // row[7] (K10-4): 1 when the camera's front lens faced the walker at this frame, 0 when it faced the way they walked.
  const backward = row[7] === 1 ? true : row[7] === 0 ? false : null
  return { up: unit(row.slice(1, 4)), turn: number(row[4]), bearing: number(row[5]), route: number(row[6]), backward }
}

// The moment of the copy that shows `routeSeconds` along the GPS route - the
// frame taken nearest then (its middle, where stepping lands) - or null when
// the track doesn't know when its frames were taken.
export function videoTimeAt(track, routeSeconds, frameSeconds = 0) {
  const rows = track?.rows
  if (!Array.isArray(rows) || !Number.isFinite(routeSeconds)) return null
  let best = null
  for (const row of rows) {
    if (!Number.isFinite(row[6])) continue
    if (!best || Math.abs(row[6] - routeSeconds) < Math.abs(best[6] - routeSeconds)) best = row
  }
  if (!best) return null
  return track.by === "frame" ? best[0] * frameSeconds + frameSeconds / 2 : best[0]
}

function nearestRow(rows, seconds) {
  let lo = 0
  let hi = rows.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (rows[mid][0] < seconds) lo = mid + 1
    else hi = mid
  }
  if (lo > 0 && Math.abs(rows[lo - 1][0] - seconds) <= Math.abs(rows[lo][0] - seconds)) lo--
  return rows[lo]
}

// The up direction at `seconds` of the copy that plays, or null - for the
// older tracks, { by: "picture" | "second", up: [[picture or seconds, x, y, z], ...] }.
export function upAt(track, seconds, frameSeconds = 0) {
  if (track?.rows) return trackAt(track, seconds, frameSeconds)?.up || null
  const rows = track?.up
  if (!Array.isArray(rows) || rows.length === 0) return null
  if (track.by === "picture") {
    if (!(frameSeconds > 0)) return null
    const picture = Math.floor(seconds / frameSeconds + 1e-3)
    let x = 0
    let y = 0
    let z = 0
    let n = 0
    for (const [i, ux, uy, uz] of rows) {
      if (Math.abs(i - picture) > AROUND) continue
      x += ux
      y += uy
      z += uz
      n++
    }
    return n ? unit([x, y, z]) : null
  }
  // Ten a second of video: the nearest.
  let lo = 0
  let hi = rows.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (rows[mid][0] < seconds) lo = mid + 1
    else hi = mid
  }
  if (lo > 0 && Math.abs(rows[lo - 1][0] - seconds) <= Math.abs(rows[lo][0] - seconds)) lo--
  return Math.abs(rows[lo][0] - seconds) <= 1 ? unit(rows[lo].slice(1, 4)) : null
}

// The turn from the view to the tilted camera, for the 360° view's shader
// (its own axes: x right, y up, looking down -z): a column-major 3×3, as
// WebGL's uniformMatrix3fv wants it. Level "ahead" is the middle of the
// picture brought level. With `turnDeg` (how far the camera had turned to the
// left since the start) the view is steady: its yaw is counted from where the
// camera faced at the start, so a camera turning in the hand doesn't swing it.
// null for nothing to undo.
export function levelMatrix(up, turnDeg = 0) {
  const level = tiltMatrix(up)
  const turn = Number(turnDeg) || 0
  if (!turn) return level
  // The view's ray, turned back by the camera's turn about the level vertical, then tilted.
  const c = Math.cos((turn * Math.PI) / 180)
  const s = Math.sin((turn * Math.PI) / 180)
  const round = new Float32Array([c, 0, s, 0, 1, 0, -s, 0, c])
  return level ? multiply(level, round) : round
}

// Column-major 3×3 product a·b.
function multiply(a, b) {
  const out = new Float32Array(9)
  for (let col = 0; col < 3; col++) {
    for (let row = 0; row < 3; row++) {
      out[col * 3 + row] = a[row] * b[col * 3] + a[3 + row] * b[col * 3 + 1] + a[6 + row] * b[col * 3 + 2]
    }
  }
  return out
}

function tiltMatrix(up) {
  if (!up) return null
  const u = unit(up)
  if (!u) return null
  // Any way up (K10-4, 2026-10-21): a camera turned on its side or upside down
  // partway through a walk still says which way was up, so its pictures are
  // turned back level like a slight lean (Quentin's Oct 19 walk had come out
  // sideways because these were left alone).
  // In the camera's axes: level up is u; level ahead is the picture's middle (x)
  // with its up-part taken out - or, when the middle points almost straight up
  // or down, the picture's left (y), so there is always a direction to face.
  const ref = Math.abs(u[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0]
  const d = ref[0] * u[0] + ref[1] * u[1] + ref[2] * u[2]
  const ahead = unit([ref[0] - d * u[0], ref[1] - d * u[1], ref[2] - d * u[2]])
  if (!ahead) return null
  const left = cross(u, ahead)
  // Camera axes from the view's: ahead = -z, left = -x, up = y.
  // A view ray (x, y, z) is -z·ahead - x·left + y·up in the camera's axes;
  // then back to the view's axes (x = -left, y = up, z = -ahead).
  const toView = ([cx, cy, cz]) => [-cy, cz, -cx]
  const columns = [
    toView(left.map((v) => -v)), // view x
    toView(u), // view y
    toView(ahead.map((v) => -v)) // view z
  ]
  return new Float32Array(columns.flat())
}

// The same 3×3 applied to a ray - what the shader does - for tests and the path line.
export function turn(matrix, [x, y, z]) {
  if (!matrix) return [x, y, z]
  const m = matrix
  return [m[0] * x + m[3] * y + m[6] * z, m[1] * x + m[4] * y + m[7] * z, m[2] * x + m[5] * y + m[8] * z]
}

function unit([x, y, z]) {
  const length = Math.hypot(x, y, z)
  return length > 1e-9 ? [x / length, y / length, z / length] : null
}

function cross([ax, ay, az], [bx, by, bz]) {
  return [ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx]
}
